import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  createRole,
  deleteRole,
  getRole,
  listPermissions,
  listRoles,
  replaceRolePermissions,
  updateRole,
} from "../../services/accessControlService";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import ConfirmationModal from "../../components/common/ConfirmationModal";

const emptyForm = { id: null, name: "", description: "", permissionIds: [] };

const RolesPage = () => {
  const { hasPermission, refreshUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canCreate =
    hasPermission("roles.create") && hasPermission("roles.assign_permissions");
  const canUpdate = hasPermission("roles.update");
  const canAssign = hasPermission("roles.assign_permissions");
  const canDelete = hasPermission("roles.delete");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [roleResponse, permissionResponse] = await Promise.all([
        listRoles(),
        listPermissions(),
      ]);
      setRoles(roleResponse.data || []);
      setPermissions(permissionResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const modules = useMemo(() => {
    const grouped = new Map();
    permissions.forEach((permission) => {
      if (!grouped.has(permission.module_key)) {
        grouped.set(permission.module_key, {
          key: permission.module_key,
          label: permission.module_label,
          permissions: [],
        });
      }
      grouped.get(permission.module_key).permissions.push(permission);
    });
    return [...grouped.values()];
  }, [permissions]);

  const openCreate = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = async (roleId) => {
    try {
      const response = await getRole(roleId);
      const role = response.data;
      setForm({
        id: role.id,
        name: role.name,
        description: role.description || "",
        permissionIds: role.permission_ids || [],
      });
      setOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load role");
    }
  };

  const togglePermission = (permission, checked) => {
    setForm((current) => {
      const selected = new Set(current.permissionIds);
      if (checked) {
        selected.add(permission.id);
        if (permission.action_key !== "view") {
          const viewPermission = permissions.find(
            (candidate) =>
              candidate.module_key === permission.module_key &&
              candidate.action_key === "view",
          );
          if (viewPermission) selected.add(viewPermission.id);
        }
      } else {
        selected.delete(permission.id);
        if (permission.action_key === "view") {
          permissions
            .filter((candidate) => candidate.module_key === permission.module_key)
            .forEach((candidate) => selected.delete(candidate.id));
        }
      }
      return { ...current, permissionIds: [...selected] };
    });
  };

  const toggleModule = (module, checked) => {
    setForm((current) => {
      const selected = new Set(current.permissionIds);
      module.permissions.forEach((permission) =>
        checked ? selected.add(permission.id) : selected.delete(permission.id),
      );
      return { ...current, permissionIds: [...selected] };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Role name is required");
      return;
    }
    try {
      setSaving(true);
      if (form.id) {
        if (canUpdate) {
          await updateRole(form.id, {
            name: form.name,
            description: form.description,
          });
        }
        if (canAssign) {
          await replaceRolePermissions(form.id, form.permissionIds);
        }
        toast.success("Role updated");
      } else {
        await createRole({
          name: form.name,
          description: form.description,
          permission_ids: form.permissionIds,
        });
        toast.success("Role created");
      }
      await refreshUser();
      setOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    try {
      setDeleting(true);
      await deleteRole(roleToDelete.id);
      toast.success("Role deleted");
      setRoleToDelete(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete role");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold text-slate-900">
            <ShieldCheck className="text-primary" /> Roles & Permissions
          </h1>
          <p className="mt-1 text-slate-500">
            Build roles from page and action permissions.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="rounded-2xl">
            <Plus size={18} className="mr-2" /> New Role
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Users</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-slate-500">
                  No roles found.
                </TableCell>
              </TableRow>
            )}
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{role.name}</div>
                  <div className="text-xs text-slate-500">{role.description}</div>
                </TableCell>
                <TableCell>{role.permission_count}</TableCell>
                <TableCell>{role.user_count}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    {(canUpdate || canAssign) && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(role.id)}>
                        <Pencil size={16} />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => setRoleToDelete(role)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Role" : "Create Role"}</DialogTitle>
            <DialogDescription>
              Select the pages and actions available to users with this role.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-semibold">Role name</label>
              <Input
                value={form.name}
                disabled={Boolean(form.id) && !canUpdate}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Description</label>
              <Input
                value={form.description}
                disabled={Boolean(form.id) && !canUpdate}
                onChange={(event) =>
                  setForm({ ...form, description: event.target.value })
                }
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-3 text-left">Page / module</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => {
                  const allSelected = module.permissions.every((permission) =>
                    form.permissionIds.includes(permission.id),
                  );
                  return (
                    <tr key={module.key} className="border-t border-slate-100">
                      <td className="p-3 align-top font-semibold text-slate-800">
                        <label className="flex items-center gap-2">
                          <Checkbox
                            checked={allSelected}
                            disabled={!canAssign && Boolean(form.id)}
                            onCheckedChange={(checked) =>
                              toggleModule(module, Boolean(checked))
                            }
                          />
                          {module.label}
                        </label>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-4">
                          {module.permissions.map((permission) => (
                            <label
                              key={permission.id}
                              className="flex items-center gap-2 text-slate-600"
                            >
                              <Checkbox
                                checked={form.permissionIds.includes(permission.id)}
                                disabled={!canAssign && Boolean(form.id)}
                                onCheckedChange={(checked) =>
                                  togglePermission(permission, Boolean(checked))
                                }
                              />
                              {permission.action_label}
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={Boolean(roleToDelete)}
        onClose={() => setRoleToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Role"
        message={
          roleToDelete
            ? `Are you sure you want to delete “${roleToDelete.name}”? This action cannot be undone.`
            : ""
        }
        confirmText="Delete Role"
        confirmButtonClass="bg-red-500 hover:bg-red-600 shadow-red-500/20"
        isLoading={deleting}
      />
    </div>
  );
};

export default RolesPage;
